import type { StandardSchemaV1 } from '@standard-schema/spec';
import type { WorkflowRun } from './db/types';
import type { Duration } from './duration';
import type { WorkflowPriority } from './priority';
import type { Schedule } from './schedule';

export enum WorkflowStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export enum StepType {
  PAUSE = 'pause',
  RUN = 'run',
  WAIT_FOR = 'waitFor',
  WAIT_UNTIL = 'waitUntil',
  DELAY = 'delay',
  POLL = 'poll',
  INVOKE_CHILD_WORKFLOW = 'invokeChildWorkflow',
}

export type InputParameters = StandardSchemaV1;
export type InferInputParameters<P extends InputParameters> = StandardSchemaV1.InferOutput<P>;

export type StartWorkflowOptions = {
  resourceId?: string;
  timeout?: number;
  retries?: number;
  expireInSeconds?: number;
  idempotencyKey?: string;
  priority?: WorkflowPriority;
  /**
   * Client-side counterpart of `workflow(..., { singleton: true })`.
   * The engine reads this from the registered definition; pass it here when
   * starting from `WorkflowClient` so the uniqueness constraint applies.
   */
  singleton?: boolean;
};

export type WorkflowOptions<I extends InputParameters> = {
  timeout?: number;
  retries?: number;
  inputSchema?: I;
  priority?: WorkflowPriority;
  /**
   * When true, at most one pending or running run of this workflow ID may exist.
   * A second `startWorkflow` throws `WorkflowRunInProgressError` until the
   * current run pauses, completes, fails, or is cancelled.
   */
  singleton?: boolean;
  /**
   * Recurring schedule. Accepts a cron expression (`'0 9 * * 1-5'`),
   * a duration string (`'5m'`, `'1 hour'`), or a `DurationObject`.
   */
  schedule?: Schedule;
  /** IANA timezone for cron expressions. Defaults to UTC. Ignored for duration-based schedules. */
  timezone?: string;
};

/** Metadata about a scheduled fire, exposed on `ctx.schedule` for runs triggered by a schedule. */
export type ScheduleContext = {
  /** Time the schedule fired this run. */
  timestamp: Date;
};

export type StepBaseContext = {
  run: <T>(stepId: string, handler: () => Promise<T>) => Promise<T>;
  waitFor: {
    <T extends InputParameters>(
      stepId: string,
      options: { eventName: string; schema?: T },
    ): Promise<InferInputParameters<T>>;
    <T extends InputParameters>(
      stepId: string,
      options: { eventName: string; timeout: number; schema?: T },
    ): Promise<InferInputParameters<T> | undefined>;
  };
  waitUntil: {
    (stepId: string, date: Date): Promise<void>;
    (stepId: string, dateString: string): Promise<void>;
    (stepId: string, options: { date: Date | string }): Promise<void>;
  };
  /** Delay execution for a duration (sugar over waitUntil). Alias: sleep. */
  delay: (stepId: string, duration: Duration) => Promise<void>;
  /** Alias for delay. */
  sleep: (stepId: string, duration: Duration) => Promise<void>;
  pause: (stepId: string) => Promise<void>;
  poll: <T>(
    stepId: string,
    conditionFn: () => Promise<T | false>,
    options?: { interval?: Duration; timeout?: Duration },
  ) => Promise<{ timedOut: false; data: T } | { timedOut: true }>;
  /**
   * Invoke a child workflow from inside the current workflow and pause until
   * the child run reaches a terminal state.
   */
  invokeChildWorkflow: {
    <TInput extends InputParameters, TOutput = unknown>(
      stepId: string,
      ref: WorkflowRef<TInput, TOutput>,
      input: InferInputParameters<TInput>,
      options?: StartWorkflowOptions,
    ): Promise<TOutput>;
    <TOutput = unknown>(
      stepId: string,
      params: {
        workflowId: string;
        input: unknown;
        resourceId?: string;
        idempotencyKey?: string;
        options?: StartWorkflowOptions;
      },
    ): Promise<TOutput>;
  };
};

/**
 * Plugin that extends the workflow step API with extra methods.
 * @template TStepBase - The step type this plugin receives (base + previous plugins).
 * @template TStepExt - The extra methods this plugin adds to step.
 */
export interface WorkflowPlugin<TStepBase = StepBaseContext, TStepExt = object> {
  name: string;
  methods: (step: TStepBase, context: WorkflowContext) => TStepExt;
  /**
   * Optional middleware around the workflow handler call. Composes in
   * registration order — the first plugin passed to `.use()` wraps everything
   * inside. Implementations MUST call `next()` exactly once.
   */
  wrap?: (context: WorkflowContext, next: () => Promise<unknown>) => Promise<unknown>;
}

export type WorkflowContext<
  TInput extends InputParameters = InputParameters,
  TStep extends StepBaseContext = StepBaseContext,
> = {
  input: InferInputParameters<TInput>;
  step: TStep;
  workflowId: string;
  runId: string;
  /** Tenant/scope identifier set when the run was started, if any. */
  resourceId?: string;
  /** Zero-based retry attempt number (= `run.retryCount`). */
  attempt: number;
  timeline: Record<string, unknown>;
  logger: WorkflowLogger;
  /** Set only for runs triggered by a recurring schedule. */
  schedule?: ScheduleContext;
};

export type WorkflowDefinition<TInput extends InputParameters = InputParameters> = {
  id: string;
  /** Widest context avoids contravariance when collecting definitions; `workflow()` still types the handler narrowly. */
  handler: (context: WorkflowContext<InputParameters, StepBaseContext>) => Promise<unknown>;
  inputSchema?: TInput;
  timeout?: number; // milliseconds
  retries?: number;
  priority?: WorkflowPriority;
  singleton?: boolean;
  schedule?: Schedule;
  timezone?: string;
  plugins?: WorkflowPlugin[];
};

export type StepInternalDefinition = {
  id: string;
  type: StepType;
  conditional: boolean;
  loop: boolean;
  isDynamic: boolean;
};

export type WorkflowInternalDefinition<TInput extends InputParameters = InputParameters> =
  WorkflowDefinition<TInput> & {
    steps: StepInternalDefinition[];
  };

/**
 * Chainable workflow factory: call as (id, handler, options) and/or use .use(plugin).
 * TStepExt is the accumulated step extension from all plugins (step = StepContext & TStepExt).
 */
export interface WorkflowFactory<TStepExt = object> {
  <I extends InputParameters = InputParameters>(
    id: string,
    handler: (context: WorkflowContext<I, StepBaseContext & TStepExt>) => Promise<unknown>,
    options?: WorkflowOptions<I>,
  ): WorkflowDefinition<I>;
  use<TNewExt>(
    plugin: WorkflowPlugin<StepBaseContext & TStepExt, TNewExt>,
  ): WorkflowFactory<TStepExt & TNewExt>;
  ref<TInput extends InputParameters = InputParameters, TOutput = unknown>(
    id: string,
    options?: { inputSchema?: TInput; singleton?: boolean },
  ): WorkflowRef<TInput, TOutput>;
}

/**
 * Lightweight workflow reference - carries the workflow ID and input type
 * but no handler code. Safe to import in API services without pulling in
 * heavy worker dependencies.
 *
 * Callable: pass a handler to create a full WorkflowDefinition.
 */
export interface WorkflowRef<
  TInput extends InputParameters = InputParameters,
  // biome-ignore lint/correctness/noUnusedVariables: phantom type carried for typed return inference
  TOutput = unknown,
> {
  (
    handler: (context: WorkflowContext<TInput, StepBaseContext>) => Promise<unknown>,
    options?: Omit<WorkflowOptions<TInput>, 'inputSchema'>,
  ): WorkflowDefinition<TInput>;
  readonly id: string;
  readonly inputSchema?: TInput;
  readonly singleton?: boolean;
}

export type WorkflowRunProgress = WorkflowRun & {
  completionPercentage: number;
  totalSteps: number;
  completedSteps: number;
};

export interface WorkflowLogger {
  log(message: string): void;
  error(message: string, ...args: unknown[]): void;
}

export type WorkflowInternalLoggerContext = {
  runId?: string;
  workflowId?: string;
};

export interface WorkflowInternalLogger {
  log(message: string, context?: WorkflowInternalLoggerContext): void;
  error(message: string, error: Error, context?: WorkflowInternalLoggerContext): void;
}

const _STEP_BASE_METHOD_TO_TYPE: Record<keyof StepBaseContext, StepType> = {
  run: StepType.RUN,
  waitFor: StepType.WAIT_FOR,
  waitUntil: StepType.WAIT_UNTIL,
  delay: StepType.DELAY,
  sleep: StepType.DELAY,
  pause: StepType.PAUSE,
  poll: StepType.POLL,
  invokeChildWorkflow: StepType.INVOKE_CHILD_WORKFLOW,
};

export const STEP_BASE_METHOD_TYPES: ReadonlyMap<string, StepType> = new Map(
  Object.entries(_STEP_BASE_METHOD_TO_TYPE),
);
